<template>
  <b-modal
    v-model="visible"
    ref="add-image-modal"
    centered
    scrollable
    @hidden="onHidden"
  >
    <template #modal-header>
      <div class="drop-header">
        <h5 :style="styleTitleDrop">Upload Images</h5>
        <b-alert
          v-model="showError"
          variant="danger"
          dismissible
          @dismissed="cleanErrors"
          class="drop-error"
        >
          {{ errorUpload.message }}
        </b-alert>
      </div>
    </template>
    <drop-zone
      @files-dropped="addFiles"
      class="drop-area"
      #default="{dropZoneActive}"
    >
      <label for="file-input">
        <span v-if="dropZoneActive">
          <span>Drop Them Here</span>
          <span class="smaller">to add them</span>
        </span>
        <span v-else>
          <span>Drag Your Images Here</span>
          <span class="smaller">
            or <strong><em>click here</em></strong> to select images
          </span>
        </span>

        <input type="file" id="file-input" multiple @change="onInputChange" />
      </label>
      <ul class="image-list" v-show="files.length">
        <file-preview
          v-for="file of files"
          :key="file.id"
          :file="file"
          tag="li"
          @remove="removeFile"
        />
      </ul>
    </drop-zone>
    <div class="drop-loader" v-show="loading">
      <div class="spinner">
        <div class="bounce1"></div>
        <div class="bounce2"></div>
        <div class="bounce3"></div>
      </div>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <b-button size="sm" variant="danger" @click="cancel">
        Cancel
      </b-button>
      <b-button
        size="sm"
        variant="success"
        @click="uploadImages().then(() => ok())"
      >
        Upload
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import dropZone from "./dropZone";
import filePreview from "./filePreview";
import mixinFileList from "@/mixins/fileList.js";
import { mapActions, mapMutations, mapGetters } from "vuex";

export default {
  mixins: [mixinFileList],
  components: { dropZone, filePreview },
  props: { showAddImageModal: Boolean },
  data() {
    return { showError: false, loading: false };
  },
  computed: {
    ...mapGetters('user', ['getUserId']),
    visible: {
      get() {
        return this.showAddImageModal;
      },
      set() {
        this.$emit("close-modal");
      }
    },
    errorUpload() {
      return this.$store.state.challenge.error;
    },
    styleTitleDrop() {
      return this.showError ? { margin: "0 0 18px" } : { margin: "0" };
    }
  },
  methods: {
    ...mapMutations("challenge", ["cleanErrors", "error"]),
    ...mapActions("challenge", ["addChallengeImage"]),
    onInputChange(e) {
      this.addFiles(e.target.files);
      e.target.value = null;
    },
    onHidden() {
      this.files = [];
      this.cleanErrors();
      this.showError = false;
    },
    async uploadImages() {
      const failedImages = [];
      this.showError = false;
      this.loading = true;

      try {
        if (!this.files.length)
          throw { message: "You have not selected any image!" };

        await Promise.allSettled(
          this.files.map(async file => {
            try {
              await this.addChallengeImage({ data: file, idParent: this.getUserId });
            } catch (error) {
              failedImages.push(file);
            }
          })
        );

        if (failedImages.length) {
          this.files = failedImages;
          throw { message: "The following files weren't uploaded. Try again" };
        }
      } catch (error) {
        this.error(error);
        this.showError = true;
        throw error;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style>
.bg-main {
  background-color: var(--color-main) !important;
}

.container-drag-drop {
  background-color: var(--color-main);
}

.close-dnd {
  background-color: transparent !important;
  border: none !important;
}

.drop-area {
  min-height: 380px;
  padding: 25px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: rgba(255, 255, 255, 0.333);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  transition: 0.2s ease;
}

.drop-area[data-active="true"] {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  background: rgba(255, 255, 255, 0.8);
}

.drop-header {
  width: 100%;
}

.drop-header .drop-error {
  margin: 0;
}

label[for="file-input"] {
  font-size: 36px;
  cursor: pointer;
  display: block;
}

label[for="file-input"] span {
  display: block;
  text-align: center;
}

label[for="file-input"] input[type="file"]:not(:focus-visible) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

label[for="file-input"] input[type="file"] {
  font-size: initial;
}

label[for="file-input"] .smaller {
  font-size: 16px;
}
.image-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  grid-template-rows: 150px;
  gap: 1rem;
  list-style: none;
}
.upload-button {
  display: block;
  appearance: none;
  border: 0;
  border-radius: 50px;
  padding: 0.75rem 3rem;
  margin: 1rem auto;
  font-size: 1.25rem;
  font-weight: bold;
  background: #369;
  color: #fff;
  text-transform: uppercase;
}

/* Loader */
.drop-loader {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;

  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  background-color: rgba(0, 0, 0, 0.8);
  background-color: rgba(33, 37, 41, 0.8);
}

.drop-loader .spinner {
  width: 100%;
  height: 100%;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
}

.drop-loader .spinner > div {
  width: 18px;
  height: 18px;
  background-color: var(--color-main);

  border-radius: 100%;
  display: inline-block;
  -webkit-animation: sk-bouncedelay 1.4s infinite ease-in-out both;
  animation: sk-bouncedelay 1.4s infinite ease-in-out both;
}

.drop-loader .spinner .bounce1 {
  -webkit-animation-delay: -0.32s;
  animation-delay: -0.32s;
}

.drop-loader .spinner .bounce2 {
  -webkit-animation-delay: -0.16s;
  animation-delay: -0.16s;
}

@-webkit-keyframes sk-bouncedelay {
  0%,
  80%,
  100% {
    -webkit-transform: scale(0);
  }
  40% {
    -webkit-transform: scale(1);
  }
}

@keyframes sk-bouncedelay {
  0%,
  80%,
  100% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  40% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
</style>
